import React from 'react'
import { useNavigate } from 'react-router-dom'

import { cn, createKeyboardClickHandler } from '@/utils'
import { buildDeliverableSetRoute } from '@/utils/routes'

import { DueStatus, MyWorkItemDTO } from '../types'
import {
  calculateDueStatus,
  calculateOverdueDays,
  formatMyWorkTimestamp,
} from '../utils/dateUtils'

import { MyWorkDueDate } from './MyWorkDueDate'
import { MyWorkDueTag } from './MyWorkDueTag'

interface MyWorkCardProps {
  item: MyWorkItemDTO
}

const getCardBorderClass = (status: DueStatus): string => {
  switch (status) {
    case DueStatus.OVERDUE:
      return 'border-t-error'
    case DueStatus.DUE_TODAY:
      return 'border-t-warning-500'
    case DueStatus.DUE_THIS_WEEK:
      return 'border-t-purple-500'
    case DueStatus.NONE:
    default:
      return ''
  }
}

export const MyWorkCard: React.FC<MyWorkCardProps> = ({ item }) => {
  const navigate = useNavigate()
  const dueStatus = calculateDueStatus(item.due_date)
  const overdueDays = calculateOverdueDays(item.due_date)
  const formattedLastActivity = formatMyWorkTimestamp(item.last_activity_date)
  const cardBorderClass = getCardBorderClass(dueStatus)

  const handleClick = () => {
    navigate(buildDeliverableSetRoute(item.id))
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={createKeyboardClickHandler(handleClick)}
      className={cn(
        'w-full rounded-lg border border-solid border-t-4 border-gray-200 bg-white p-2xl shadow-sm hover:shadow-lg transition-shadow gap-xl',
        'flex flex-col cursor-pointer text-left',
        cardBorderClass
      )}
    >
      <div className="space-y-3">
        {/* top row */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <MyWorkDueTag status={dueStatus} />
            {overdueDays && overdueDays > 0 && (
              <span className="text-xs text-gray-700 leading-none">
                {overdueDays} {overdueDays === 1 ? 'day' : 'days'}
              </span>
            )}
          </div>

          <div className="shrink-0">
            <MyWorkDueDate dueDate={item.due_date} status={dueStatus} />
          </div>
        </div>

        <div className="gap-x-sm text-base">
          <span className="text-lg font-semibold text-gray-900 leading-snug break-words line-clamp-2">
            {item.deliverable_set_name}
          </span>
          <div>
            <span className="text-primary-900 text-sm">Project:</span>{' '}
            <span className="text-primary-900 font-medium break-words line-clamp-2 text-sm">
              {item.project_name}
            </span>
          </div>
        </div>

        <div className="gap-x-sm text-base">
          <div>
            <span className="text-primary-900 text-sm">Open comments:</span>{' '}
            <span className="text-primary-900 font-medium text-sm">
              {item.snapshot_comment_open}
            </span>
          </div>

          <div>
            <span className="text-primary-900 text-sm">Last activity:</span>{' '}
            <span className="text-primary-900 font-medium text-sm">
              {formattedLastActivity}
            </span>
          </div>
        </div>
      </div>
    </button>
  )
}
